import { Config } from "@planetscale/database";
import { useState } from "react";
import { redirect, useNavigate } from "react-router";
import { requireSess } from "~/auth.server";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { trpc } from "~/TrpcClient";
import { GameVer, Lang, lstring, useRealm, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, MetaArgs, OurLoaderArgs } from "~/util/Loaders";
import { homePage } from "~/util/titles";
import { Heading3 } from "~/components/ui/Heading";
import { Button } from "~/components/ui/Button";
import css from "./PageForm.module.css";
import { z } from "zod";
import { logPageView } from "~/util/stats";

const PageQuery = z.object({
  type: z.optional(z.string()),
  id: z.optional(z.string()),
});

async function makeData(args: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(args);
  //
  const sess = requireSess(args.req.sess);
  if (!sess) {
    return redirect("/login");
  }

  const query = PageQuery.parse(
    Object.fromEntries(args.url.searchParams.entries())
  );

  return { query };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

function makeMeta(
  cx: MetaArgs<LoaderData>,
  opts: { title: lstring; canonicalPath?: string }
) {
  return {
    title: `${cx.t({ en: "Servers", ru: "Сервера" })} - ${homePage(cx.realm)}`,

    ogTitle: `Lineage 2 Item Sets`,
  };
}

$PAGE({
  path: "pages/create",
  loader: $LOADER?.(async (args) => {
    return makeData(args, {});
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Create Page", ru: "Создать Страницу" },
      canonicalPath: "/pages/create",
    }),
  render: ({ data }) => {
    const t = useT();
    const realm = useRealm();
    const turl = useTurl();
    const navigate = useNavigate();
    const pageTitle = t({ en: "Create Page", ru: "Создать Страницу" });
    const { query } = data;
    const [state, setState] = useState({
      title: "",
      content: "",
      lang: realm.lang,
      game: [realm.game],
      objType: query.type,
      objId: query.id ? parseInt(query.id) : undefined,
    });

    function handleChange(
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) {
      const { name, value } = e.target;
      setState((prevState) => ({ ...prevState, [name]: value }));
    }

    function handleCheckboxChange(value: GameVer) {
      setState((prev) => ({
        ...prev,
        game: prev.game.includes(value)
          ? prev.game.filter((g) => g !== value)
          : [...prev.game, value],
      }));
    }

    const createPage = trpc.pages.createPage.useMutation();

    async function doCreate() {
      const result = await createPage.mutateAsync(state);
      switch (result.t) {
        case "success": {
          navigate(`/pages/${result.data.pageId}`);
          break;
        }
      }
    }

    const disabled = () => !state.content.trim() || !state.title.trim();

    return (
      <div>
        <div className="content">
          <Breadcrumb
            items={[
              {
                title: t({ en: "Pages", ru: "Страницы" }),
                url: turl("/pages"),
              },
              { title: pageTitle, url: turl(`/pages/create`) },
            ]}
          />
          <PageTitle>{pageTitle}</PageTitle>

          <form
            className={css.root}
            onSubmit={(e) => {
              if (disabled()) {
                return;
              }

              e.preventDefault();
              doCreate();
            }}
            autoComplete="off"
          >
            <div className="columns">
              <div className="column">
                <select
                  className={css.select}
                  id="lang"
                  name="lang"
                  value={state.lang}
                  onChange={handleChange}
                >
                  {Lang.options.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
              <div className="column">
                <div className={css.checkboxBlock}>
                  {GameVer.options.map((ver) => (
                    <label key={ver} className={css.checkbox}>
                      <input
                        type="checkbox"
                        value={ver}
                        checked={state.game.includes(ver)}
                        onChange={() => handleCheckboxChange(ver)}
                      />
                      {ver}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="columns">
              <div className="column">
                <input
                  className={css.input}
                  type="text"
                  name="objType"
                  value={state.objType}
                  onChange={handleChange}
                />
              </div>
              <div className="column">
                <input
                  className={css.input}
                  type="text"
                  name="objId"
                  value={state.objId}
                  onChange={handleChange}
                />
              </div>
            </div>

            <Heading3>{t({ en: "Heading", ru: "Заголовок" })}</Heading3>
            <input
              className={css.input}
              type="text"
              name="title"
              value={state.title}
              onChange={handleChange}
            />

            <Heading3>{t({ en: "Content", ru: "Контент" })}</Heading3>
            <textarea
              className={css.textarea}
              name="content"
              value={state.content}
              onChange={handleChange}
            />
            <div style={{ marginTop: 20 }}>
              <Button type="submit" disabled={disabled()}>
                {t({ en: "Create", ru: "Создать" })}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  },
});
